import { useMemo, useState } from 'react';
import { Plus, Search, X } from 'lucide-react';
import type { MemberPublic } from '@/lib/db/types';
import { Avatar } from '@/components/shared/Avatar';

export interface GuestEntry {
  name: string;
  handicap?: number;
}

interface Props {
  members: MemberPublic[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  guests?: GuestEntry[];
  guestDraft?: string;
  guestHandicapDraft?: string;
  onGuestDraftChange?: (value: string) => void;
  onGuestHandicapDraftChange?: (value: string) => void;
  onAddGuest?: () => void;
  onRemoveGuest?: (index: number) => void;
  onUpdateGuestHandicap?: (index: number, handicap: number | undefined) => void;
  showGuests?: boolean;
  title?: string;
  searchPlaceholder?: string;
}

export function filterMembersByQuery(members: MemberPublic[], query: string): MemberPublic[] {
  const q = query.trim().toLowerCase();
  if (!q) return members;
  return members.filter(
    (m) => m.name.toLowerCase().includes(q) || m.handle.toLowerCase().includes(q),
  );
}

export function InvitePlayersPanel({
  members,
  selectedIds,
  onToggle,
  guests,
  guestDraft,
  guestHandicapDraft,
  onGuestDraftChange,
  onGuestHandicapDraftChange,
  onAddGuest,
  onRemoveGuest,
  onUpdateGuestHandicap,
  showGuests = true,
  title = 'Invite players',
  searchPlaceholder = 'Search members…',
}: Props) {
  const [search, setSearch] = useState('');

  const filtered = useMemo(
    () => filterMembersByQuery(members, search),
    [members, search],
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-bold uppercase tracking-wider text-mute">{title}</div>
        {selectedIds.length > 0 && (
          <div className="text-[10px] font-semibold text-gold-deep">{selectedIds.length} selected</div>
        )}
      </div>

      <div className="flex items-center gap-2 rounded-[10px] border border-line bg-paper px-3 py-2.5">
        <Search size={15} className="shrink-0 text-mute" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          className="min-w-0 flex-1 border-none bg-transparent text-sm text-ink outline-none"
        />
        {search && (
          <button type="button" onClick={() => setSearch('')} className="bt-flat flex text-mute" aria-label="Clear search">
            <X size={14} />
          </button>
        )}
      </div>

      <div className="h-52 min-h-52 overflow-y-auto rounded-[10px] border border-line bg-cream/40 p-1">
        {filtered.length === 0 ? (
          <div className="flex h-full min-h-[12rem] items-center justify-center px-3 text-center text-[13px] text-mute">
            {search.trim() ? `No members match "${search.trim()}"` : 'No members available'}
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((m) => {
              const on = selectedIds.includes(m.id);
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => onToggle(m.id)}
                  className={`flex w-full items-center gap-3 rounded-xl p-2 transition-colors ${
                    on ? 'bg-navy/10 ring-2 ring-navy' : 'bg-cream'
                  }`}
                >
                  <Avatar member={m} size={32} />
                  <div className="min-w-0 flex-1 text-left">
                    <span className="block truncate text-sm font-semibold text-navy">{m.name}</span>
                    <span className="text-[10px] text-mute">
                      @{m.handle}
                      {m.handicap != null ? ` · HCP ${m.handicap.toFixed(1)}` : ''}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {showGuests !== false && (
        <div>
          <div className="mb-2 text-xs font-bold uppercase tracking-wider text-mute">Guests</div>
          <div className="flex gap-2">
            <input
              value={guestDraft || ''}
              onChange={(e) => onGuestDraftChange?.(e.target.value)}
              placeholder="Guest name"
              className="min-w-0 flex-1 rounded-xl border border-line bg-paper px-3 py-2 text-sm outline-none focus:border-gold"
            />
            <input
              type="number"
              step="0.1"
              value={guestHandicapDraft ?? ''}
              onChange={(e) => onGuestHandicapDraftChange?.(e.target.value)}
              placeholder="HCP"
              className="w-16 rounded-xl border border-line bg-paper px-2 py-2 text-sm outline-none focus:border-gold"
            />
            <button type="button" onClick={() => onAddGuest?.()} className="rounded-xl bg-line px-3 py-2">
              <Plus size={16} />
            </button>
          </div>
          {(guests || []).map((g, i) => (
            <div key={i} className="mt-2 inline-flex items-center gap-1 rounded-full bg-line py-1 pl-2.5 pr-1 text-xs">
              <span>{g.name}</span>
              {g.handicap != null && !Number.isNaN(g.handicap) && (
                <span className="text-mute">· {g.handicap}</span>
              )}
              {onUpdateGuestHandicap && (
                <input
                  type="number"
                  step="0.1"
                  value={g.handicap ?? ''}
                  onChange={(e) => {
                    const v = e.target.value.trim();
                    onUpdateGuestHandicap(i, v ? parseFloat(v) : undefined);
                  }}
                  placeholder="HCP"
                  className="ml-1 w-12 rounded border border-line bg-paper px-1 py-0.5 text-[10px]"
                />
              )}
              <button type="button" onClick={() => onRemoveGuest?.(i)} className="p-0.5">
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
